// ButtonRoot.tsx
import { cn } from '@module/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import { TONE_BG_CLASS, TONE_BORDER_CLASS } from './colors';
import {
  ButtonContext,
  type ButtonContextValue,
  type ButtonHeight,
  type ButtonTone,
  type ButtonVariant,
} from './context/useButton';
import { PendingSpinner } from './PendingSpinner';
import { buttonVariants } from './styles';

type Native = React.ButtonHTMLAttributes<HTMLButtonElement>;

type BaseProps = Omit<Native, 'disabled'> & {
  asChild?: boolean;
  id?: string;
  variant?: ButtonVariant;
  height?: ButtonHeight; // default 'h48'
  tone?: ButtonTone; // friendly tone key (affects bg/border only)
  disabled?: boolean;
  isIconOnly?: boolean;
  isRound?: boolean;
  isPending?: boolean;
  /** 기본 overlay 중앙 표시, 교체 가능 */
  pendingIndicator?: React.ReactNode;
  /** overlay(기본) | replace | inline-start */
  pendingStrategy?: 'overlay' | 'replace' | 'inline-start';
};

// Icon-only a11y XOR
type IconOnlyA11y =
  | (BaseProps & { isIconOnly: true; 'aria-label': string; 'aria-labelledby'?: never })
  | (BaseProps & { isIconOnly: true; 'aria-labelledby': string; 'aria-label'?: never });

type NonIconOnly = BaseProps & { isIconOnly?: false };

export type ButtonRootProps = IconOnlyA11y | NonIconOnly;

export const ButtonRoot = React.forwardRef<HTMLButtonElement, ButtonRootProps>(
  (
    {
      asChild,
      id,
      variant = 'primary',
      height = 'h48',
      tone,
      disabled,
      isIconOnly,
      isRound = false,
      className,
      isPending = false,
      pendingIndicator,
      pendingStrategy = 'overlay',
      type = 'button',
      children,
      ...rest
    },
    ref,
  ) => {
    const autoId = React.useId();
    const buttonId = id ?? autoId;
    const Comp = asChild ? Slot : ('button' as const);

    // pressed state (for touch/WebView; harmless on desktop)
    const [pressed, setPressed] = React.useState(false);
    const setPressedSafe = (v: boolean) => {
      if (!disabled) setPressed(v);
    };

    const handlePointerDown: React.PointerEventHandler<HTMLButtonElement> = (e) => {
      rest?.onPointerDown?.(e);
      setPressedSafe(true);
    };
    const handlePointerUp: React.PointerEventHandler<HTMLButtonElement> = (e) => {
      rest?.onPointerUp?.(e);
      setPressedSafe(false);
    };
    const handlePointerCancel: React.PointerEventHandler<HTMLButtonElement> = (e) => {
      rest?.onPointerCancel?.(e);
      setPressedSafe(false);
    };
    const handlePointerLeave: React.PointerEventHandler<HTMLButtonElement> = (e) => {
      rest?.onPointerLeave?.(e);
      setPressedSafe(false);
    };

    // tone application: primary → bg, secondary/tertiary/round → border, others (chat) → none
    const toneClassByVariant = (() => {
      if (!tone) return undefined;
      if (variant === 'primary') return TONE_BG_CLASS[tone];
      if (variant === 'secondary' || variant === 'tertiary') return TONE_BORDER_CLASS[tone];
      return undefined;
    })();

    // pending 시 상호작용 차단(기본 동작)
    const actuallyDisabled = Boolean(disabled || isPending);

    // pending 표시 노드
    const spinnerSizeByHeight: Record<ButtonHeight, number> = {
      h56: 24,
      h48: 24,
      h40: 20,
      h32: 16,
      h28: 14,
      small: 16,
      medium: 16,
    };
    const defaultPendingNode = <PendingSpinner size={spinnerSizeByHeight[height]} />;
    const pendingNode = pendingIndicator ?? defaultPendingNode;

    const ctx: ButtonContextValue = {
      id: buttonId,
      variant,
      height,
      tone,
      disabled: actuallyDisabled,
      isPending,
    };

    const content = React.useMemo(() => {
      if (pendingStrategy === 'replace') {
        return isPending ? pendingNode : children;
      }
      if (pendingStrategy === 'inline-start') {
        return (
          <span className='inline-flex items-center gap-1'>
            {isPending && pendingNode}
            <span className={cn(isPending ? 'opacity-70' : undefined)}>{children}</span>
          </span>
        );
      }
      return (
        <>
          <span
            className={cn('inline-flex items-center gap-1', isPending ? 'opacity-0' : undefined)}
            aria-hidden={isPending || undefined}
          >
            {children}
          </span>
          {isPending && (
            <span className='pointer-events-none absolute inset-0 grid place-items-center'>{pendingNode}</span>
          )}
        </>
      );
    }, [pendingStrategy, isPending, pendingNode, children]);

    return (
      <ButtonContext.Provider value={ctx}>
        <Comp
          ref={ref as React.Ref<HTMLButtonElement>}
          id={buttonId}
          data-variant={variant}
          data-height={height}
          data-tone={tone}
          data-disabled={actuallyDisabled || undefined}
          data-pressed={pressed || undefined}
          data-pending={isPending || undefined}
          aria-busy={isPending || undefined}
          className={cn(
            buttonVariants({ variant, height, isIconOnly, isRound }),
            toneClassByVariant,
            asChild && actuallyDisabled ? 'pointer-events-none' : undefined,
            className,
          )}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onPointerLeave={handlePointerLeave}
          {...(!asChild
            ? { type, disabled: actuallyDisabled }
            : {
                'aria-disabled': actuallyDisabled,
                tabIndex: actuallyDisabled ? -1 : rest?.tabIndex,
              })}
          {...rest}
        >
          {content}
        </Comp>
      </ButtonContext.Provider>
    );
  },
);

ButtonRoot.displayName = 'Button.Root';
